import { applyPatch, areOidsRelated, assert, assignOid, cloneDeep, compareTimestampSchemaVersions, getWallClockTime, } from '@verdant-web/common';
export class EntityMetadata {
    constructor({ oid, ctx, confirmedOperations, pendingOperations, baseline, }) {
        this.baseline = null;
        // these must be kept in timestamp order.
        this.confirmedOperations = [];
        this.pendingOperations = [];
        /**
         * Compute the current view of the entity.
         */
        this.computeView = (omitPending = false) => {
            var _a, _b, _c, _d, _e, _f, _g;
            const base = cloneDeep((_b = (_a = this.baseline) === null || _a === void 0 ? void 0 : _a.snapshot) !== null && _b !== void 0 ? _b : undefined);
            const baselineTimestamp = (_d = (_c = this.baseline) === null || _c === void 0 ? void 0 : _c.timestamp) !== null && _d !== void 0 ? _d : null;
            const confirmedResult = this.applyOperations(
            // apply ops to baseline
            base, 
            // deleted if there's no baseline
            !base, 
            // we're applying confirmed ops first
            this.confirmedOperations, 
            // latest timestamp is the baseline timestamp, if any
            baselineTimestamp, 
            // only apply ops after the baseline timestamp
            baselineTimestamp);
            // now's the time to declare we saw the future if we did.
            if (confirmedResult.futureSeen) {
                this.ctx.globalEvents.emit('futureSeen', confirmedResult.futureSeen);
            }
            const pendingResult = omitPending
                ? confirmedResult
                : this.applyOperations(confirmedResult.view, confirmedResult.deleted, 
                // now we're applying pending operations
                this.pendingOperations, 
                // keep our latest timestamp up to date
                confirmedResult.latestTimestamp, 
                // we don't use after for pending ops, they're all
                // logically in the future
                null);
            // before letting this data out into the wild, we need
            // to associate its oid
            if (pendingResult.view) {
                assignOid(pendingResult.view, this.oid);
            }
            // note whether confirmed data has an operation/baseline from the current
            // schema or not.
            const fromOlderVersion = !!confirmedResult.latestTimestamp &&
                compareTimestampSchemaVersions(confirmedResult.latestTimestamp, this.ctx.getNow()) < 0;
            const empty = !this.baseline &&
                !this.pendingOperations.length &&
                !this.confirmedOperations.length;
            if (empty) {
                this.ctx.log('warn', `Tried to load Entity ${this.oid} with no data`);
            }
            const updatedAtTimestamp = (_f = (_e = pendingResult.latestTimestamp) !== null && _e !== void 0 ? _e : confirmedResult.latestTimestamp) !== null && _f !== void 0 ? _f : baselineTimestamp;
            const updatedAt = updatedAtTimestamp
                ? getWallClockTime(updatedAtTimestamp)
                : 0;
            if (!pendingResult.view && !pendingResult.deleted && !empty) {
                this.ctx.log('warn', `Entity ${this.oid} has no view, no deleted flag, and not empty`);
                debugger;
            }
            return {
                view: (_g = pendingResult.view) !== null && _g !== void 0 ? _g : undefined,
                deleted: pendingResult.deleted,
                empty,
                fromOlderVersion,
                updatedAt,
            };
        };
        this.addBaseline = (baseline) => {
            var _a;
            // opt out if our baseline is newer
            if (this.baseline && this.baseline.timestamp >= baseline.timestamp) {
                return;
            }
            this.baseline = baseline;
            // we can now drop any confirmed ops older than the baseline
            while (((_a = this.confirmedOperations[0]) === null || _a === void 0 ? void 0 : _a.timestamp) < baseline.timestamp) {
                this.confirmedOperations.shift();
            }
        };
        /**
         * @returns total number of new operations added
         */
        this.addConfirmedOperations = (operations) => {
            let totalAdded = 0;
            // the operations must be inserted in timestamp order
            for (const op of operations) {
                const index = this.confirmedOperations.findIndex((o) => o.timestamp >= op.timestamp);
                if (index !== -1) {
                    // ensure we don't have a duplicate
                    if (this.confirmedOperations[index].timestamp !== op.timestamp) {
                        // otherwise, insert at the right place
                        this.confirmedOperations.splice(index, 0, op);
                        totalAdded++;
                    }
                }
                else {
                    // otherwise, append
                    this.confirmedOperations.push(op);
                    totalAdded++;
                }
                // FIXME: seems inefficient
                // remove this incoming op from pending if it's in there
                const pendingPrior = this.pendingOperations.length;
                this.pendingOperations = this.pendingOperations.filter((pendingOp) => op.timestamp !== pendingOp.timestamp);
                totalAdded -= pendingPrior - this.pendingOperations.length;
            }
            return totalAdded;
        };
        this.addPendingOperation = (operation) => {
            // we can assume pending ops are always newer
            this.pendingOperations.push(operation);
        };
        this.applyOperations = (base, deleted, operations, latestTimestamp, after) => {
            let futureSeen = undefined;
            const now = this.ctx.getNow();
            for (const op of operations) {
                // ignore ops before our after cutoff
                if (after && op.timestamp <= after) {
                    continue;
                }
                // don't apply future ops
                if (compareTimestampSchemaVersions(op.timestamp, now) > 0) {
                    futureSeen = op.timestamp;
                    continue;
                }
                // we don't actually delete the view when a delete op
                // comes in. the view remains useful for calculating
                // undo operations.
                if (op.data.op === 'delete') {
                    deleted = true;
                }
                else {
                    base = applyPatch(base, op.data);
                    if (op.data.op === 'initialize') {
                        deleted = false;
                    }
                }
                // track the latest timestamp
                if (!latestTimestamp || op.timestamp > latestTimestamp) {
                    latestTimestamp = op.timestamp;
                }
            }
            return {
                view: base,
                latestTimestamp: latestTimestamp !== null && latestTimestamp !== void 0 ? latestTimestamp : null,
                deleted,
                futureSeen,
            };
        };
        assert(oid, 'oid is required');
        this.ctx = ctx;
        this.oid = oid;
        if (confirmedOperations) {
            this.confirmedOperations = confirmedOperations;
        }
        if (pendingOperations) {
            this.pendingOperations = pendingOperations;
        }
        if (baseline) {
            this.baseline = baseline;
        }
    }
}
/**
 * Represents the metadata for a group of entities underneath a Document.
 * Metadata is separated out this way so that these classes can be
 * garbage collected when the root Document goes out of scope.
 */
export class EntityFamilyMetadata {
    constructor({ ctx, onPendingOperations, rootOid, }) {
        this.entities = new Map();
        this.get = (oid) => {
            assert(oid, 'oid is required');
            if (!this.entities.has(oid)) {
                this.entities.set(oid, new EntityMetadata({ oid, ctx: this.ctx }));
            }
            return this.entities.get(oid);
        };
        this.getAllOids = () => {
            return Array.from(this.entities.keys());
        };
        this.addConfirmedData = ({ baselines = [], operations = {}, isLocal = false, }) => {
            var _a;
            const changes = {};
            for (const baseline of baselines) {
                if (!areOidsRelated(this.rootOid, baseline.oid)) {
                    throw new Error(`Invalid baseline for entity ${this.rootOid}: ` +
                        JSON.stringify(baseline));
                }
                this.get(baseline.oid).addBaseline(baseline);
            }
            for (const [oid, ops] of Object.entries(operations)) {
                if (!areOidsRelated(this.rootOid, oid)) {
                    throw new Error(`Invalid operations for entity ${this.rootOid}: ` +
                        JSON.stringify(ops));
                }
                const added = this.get(oid).addConfirmedOperations(ops);
                if (added !== 0) {
                    (_a = changes[oid]) !== null && _a !== void 0 ? _a : (changes[oid] = { oid, isLocal });
                }
            }
            return Object.values(changes);
        };
        /**
         * Adds local, unconfirmed operations to the system.
         * The API is different here to streamline for the way
         * local changes are usually handled, as a list.
         */
        this.addPendingData = (operations) => {
            var _a;
            var _b;
            const changes = {};
            for (const op of operations) {
                this.get(op.oid).addPendingOperation(op);
                (_a = changes[_b = op.oid]) !== null && _a !== void 0 ? _a : (changes[_b] = { oid: op.oid, isLocal: true });
            }
            this.onPendingOperations(operations);
            return Object.values(changes);
        };
        this.replaceAllData = ({ operations = {}, baselines = [], }) => {
            var _a, _b;
            var _c;
            const oids = Array.from(this.entities.keys());
            this.entities.clear();
            const changes = {};
            // changes apply to all the entities we removed things from, too
            for (const oid of oids) {
                changes[oid] = { oid, isLocal: false };
            }
            for (const baseline of baselines) {
                if (!areOidsRelated(this.rootOid, baseline.oid)) {
                    throw new Error(`Invalid baseline for entity ${this.rootOid}: ` +
                        JSON.stringify(baseline));
                }
                this.get(baseline.oid).addBaseline(baseline);
                (_a = changes[_c = baseline.oid]) !== null && _a !== void 0 ? _a : (changes[_c] = { oid: baseline.oid, isLocal: false });
            }
            for (const [oid, ops] of Object.entries(operations)) {
                if (!areOidsRelated(this.rootOid, oid)) {
                    throw new Error(`Invalid operations for entity ${this.rootOid}: ` +
                        JSON.stringify(ops));
                }
                this.get(oid).addConfirmedOperations(ops);
                (_b = changes[oid]) !== null && _b !== void 0 ? _b : (changes[oid] = { oid, isLocal: false });
            }
            return Object.values(changes);
        };
        this.ctx = ctx;
        this.rootOid = rootOid;
        this.onPendingOperations = onPendingOperations;
    }
}
//# sourceMappingURL=EntityMetadata.js.map